import { type Filter } from "@garimpa/lib/facetFilter";
import { isPlainObject } from "@garimpa/lib/isPlainObject";
import { normalizeStringArray } from "@garimpa/lib/normalizeStringArray";
import {
  filterToReleaseSearchFilter,
  isReleaseSearchFilterEmpty,
  releaseSearchFilterToFilter,
  type FacetedSearchState,
  type RangeMatcher,
  type ReleaseSearchFilter,
  type ReleaseSearchFilterKey,
  type StringMatcher,
} from "@garimpa/lib/searchState";
import { valueToArray } from "@garimpa/lib/valueToArray";

type SearchRecord = Record<string, unknown>;

export const RELEASE_GROUP_FILTER_KEYS = [
  "format",
  "type",
  "genre",
  "style",
  "country",
  "artist.country",
  "label",
  "company",
  "year",
] as const satisfies readonly ReleaseSearchFilterKey[];

export const ARTIST_DISCOGRAPHY_FILTER_KEYS = [
  "format",
  "country",
  "label",
  "company",
  "year",
] as const satisfies readonly ReleaseSearchFilterKey[];

export const COMPANY_DISCOGRAPHY_FILTER_KEYS = [
  "format",
  "genre",
  "style",
  "country",
  "label",
  "company",
  "year",
] as const satisfies readonly ReleaseSearchFilterKey[];

export const WORK_RELEASE_FILTER_KEYS = [
  "format",
  "formatSet",
  "country",
  "label",
  "company",
  "tag",
  "year",
] as const satisfies readonly ReleaseSearchFilterKey[];

export const OFFERS_FILTER_KEYS = [
  "genre",
  "style",
  "year",
] as const satisfies readonly ReleaseSearchFilterKey[];

export const ALL_MATCH_FILTER_KEYS = ["formatSet", "label", "company", "tag"] as const;

const STRING_ROOT_KEYS = [
  "format",
  "formatSet",
  "type",
  "genre",
  "style",
  "country",
  "label",
  "company",
  "tag",
] as const;

type StringRootKey = (typeof STRING_ROOT_KEYS)[number];

export type FacetedSearchUrlConfig = {
  keys: readonly ReleaseSearchFilterKey[];
  defaultSort: string;
  allKeys?: readonly StringRootKey[];
};

export const ARTIST_DISCOGRAPHY_URL_CONFIG = {
  keys: ARTIST_DISCOGRAPHY_FILTER_KEYS,
  defaultSort: "oldest",
  allKeys: ALL_MATCH_FILTER_KEYS,
} as const satisfies FacetedSearchUrlConfig;

export const COMPANY_DISCOGRAPHY_URL_CONFIG = {
  keys: COMPANY_DISCOGRAPHY_FILTER_KEYS,
  defaultSort: "popular",
  allKeys: ALL_MATCH_FILTER_KEYS,
} as const satisfies FacetedSearchUrlConfig;

export const WORK_RELEASE_URL_CONFIG = {
  keys: WORK_RELEASE_FILTER_KEYS,
  defaultSort: "date-desc",
  allKeys: ALL_MATCH_FILTER_KEYS,
} as const satisfies FacetedSearchUrlConfig;

export const WORK_RELEASE_FACET_URL_CONFIG = {
  keys: WORK_RELEASE_FILTER_KEYS.filter((key) => key !== "format"),
  defaultSort: "date-desc",
  allKeys: ALL_MATCH_FILTER_KEYS,
} as const satisfies FacetedSearchUrlConfig;

export const OFFERS_URL_CONFIG = {
  keys: OFFERS_FILTER_KEYS,
  defaultSort: "price_drop",
  allKeys: ALL_MATCH_FILTER_KEYS,
} as const satisfies FacetedSearchUrlConfig;

// Grouped discographies facet `year` by work/original year. Work release pages
// facet `year` by release/pressing year, so carrying it would over-filter.
export const GROUPED_DISCOGRAPHY_WORK_HANDOFF_EXCLUDED_KEYS = [
  "year",
] as const satisfies readonly ReleaseSearchFilterKey[];

export const SEARCH_PAGE_URL_CONFIG = {
  keys: RELEASE_GROUP_FILTER_KEYS,
  defaultSort: "popular",
  allKeys: ALL_MATCH_FILTER_KEYS,
} as const satisfies FacetedSearchUrlConfig;

function pickStringArray(value: unknown): string[] {
  return normalizeStringArray(value);
}

function numberFromParamValue(value: unknown): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") return Number(value);
  return NaN;
}

export function pickNumberArray(value: unknown): number[] | undefined {
  const values = valueToArray(value)
    .map(numberFromParamValue)
    .filter((item) => Number.isFinite(item));
  return values.length ? values.slice(0, 2) : undefined;
}

export function yearBoundsFromParam(
  year: readonly number[] | undefined,
): [number | undefined, number | undefined] {
  if (!year?.length) return [undefined, undefined];
  if (year.length === 1) return [year[0], year[0]];
  return [year[0], year[1]];
}

export function yearRangeToParam(
  yearFrom: number | undefined,
  yearTo: number | undefined,
): number[] | undefined {
  if (yearFrom == null && yearTo == null) return undefined;
  if (yearFrom != null && yearTo != null) {
    return yearFrom === yearTo ? [yearFrom] : [yearFrom, yearTo];
  }
  return [yearFrom ?? yearTo!];
}

function yearParamToMatcher(value: unknown): RangeMatcher | undefined {
  const years = pickNumberArray(value);
  if (!years?.length) return undefined;
  if (years.length === 1) return [years[0]!];
  return [years[0]!, years[1]!];
}

function matcherToParam(matcher: StringMatcher | undefined): string | string[] | undefined {
  if (!matcher) return undefined;
  if (typeof matcher === "string") return matcher || undefined;
  if (Array.isArray(matcher)) return matcher.length ? matcher : undefined;
  return matcher.all.length ? matcher.all : undefined;
}

function rangeToParam(range: RangeMatcher | undefined): number | number[] | undefined {
  if (!range) return undefined;
  if (range.length === 1) return range[0];
  return range.filter((value): value is number => value != null);
}

function setFilterMatcher(
  filter: ReleaseSearchFilter,
  key: ReleaseSearchFilterKey,
  matcher: StringMatcher,
) {
  if (key === "artist.country") {
    filter.artist = { ...filter.artist, country: matcher };
    return;
  }
  if (key !== "year") {
    filter[key] = matcher;
  }
}

function getFilterMatcher(
  filter: ReleaseSearchFilter,
  key: ReleaseSearchFilterKey,
): StringMatcher | undefined {
  if (key === "artist.country") return filter.artist?.country;
  if (key === "year") return undefined;
  return filter[key];
}

function mergeFilter(target: ReleaseSearchFilter, source: ReleaseSearchFilter | undefined) {
  if (!source) return;
  for (const key of STRING_ROOT_KEYS) {
    if (source[key] !== undefined) target[key] = source[key];
  }
  if (source.artist?.country !== undefined) {
    target.artist = { ...target.artist, country: source.artist.country };
  }
  if (source.year !== undefined) target.year = source.year;
  if (Array.isArray(source.OR) && source.OR.length) {
    target.OR = (target.OR ?? []).concat(source.OR);
  }
}

function rootMatcher(
  values: string[],
  key: StringRootKey,
  allKeys: ReadonlySet<StringRootKey>,
): StringMatcher | undefined {
  if (!values.length) return undefined;
  const shouldMatchAllTerms = allKeys.has(key);
  if (values.length === 1) return values[0]!;
  if (shouldMatchAllTerms) return { all: values };
  return values;
}

export function pickReleaseSearchFilter(
  value: unknown,
  config: Pick<FacetedSearchUrlConfig, "keys" | "allKeys">,
): ReleaseSearchFilter | undefined {
  const raw = typeof value === "string" ? parseJsonObject(value) : value;
  if (!isPlainObject(raw)) return undefined;
  const filter = raw as ReleaseSearchFilter;
  const converted = releaseSearchFilterToFilter(filter, {
    allowed: config.keys,
    allTermKeys: config.allKeys ?? ALL_MATCH_FILTER_KEYS,
  });
  return filterToReleaseSearchFilter(converted, {
    allTermKeys: config.allKeys ?? ALL_MATCH_FILTER_KEYS,
  });
}

function parseJsonObject(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

export function filterFromRootParams(
  search: SearchRecord,
  config: Pick<FacetedSearchUrlConfig, "keys" | "allKeys">,
): ReleaseSearchFilter | undefined {
  const allowed = new Set<ReleaseSearchFilterKey>(config.keys);
  const allKeys = new Set<StringRootKey>(config.allKeys ?? ALL_MATCH_FILTER_KEYS);
  const filter: ReleaseSearchFilter = {};

  for (const key of STRING_ROOT_KEYS) {
    if (!allowed.has(key)) continue;
    const matcher = rootMatcher(pickStringArray(search[key]), key, allKeys);
    if (matcher) setFilterMatcher(filter, key, matcher);
  }

  if (allowed.has("artist.country")) {
    const origin = rootMatcher(pickStringArray(search.origin), "country", allKeys);
    if (origin) setFilterMatcher(filter, "artist.country", origin);
  }

  if (allowed.has("year")) {
    const year = yearParamToMatcher(search.year);
    if (year) filter.year = year;
  }

  return isReleaseSearchFilterEmpty(filter) ? undefined : filter;
}

export function buildFacetedSearchState(
  search: SearchRecord,
  config: FacetedSearchUrlConfig,
): FacetedSearchState | undefined {
  const filter: ReleaseSearchFilter = {};
  mergeFilter(filter, search.filter as ReleaseSearchFilter | undefined);
  mergeFilter(filter, filterFromRootParams(search, config));

  const state: FacetedSearchState = {};
  if (typeof search.q === "string" && search.q) state.q = search.q;
  if (typeof search.sort === "string" && search.sort !== config.defaultSort)
    state.sort = search.sort;
  if (typeof search.page === "number" && search.page > 1) state.page = search.page;
  if (!isReleaseSearchFilterEmpty(filter)) state.filter = filter;
  return hasFacetedSearchState(state) ? state : undefined;
}

function hasFacetedSearchState(state: FacetedSearchState): boolean {
  const hasQuery = state.q !== undefined;
  const hasSort = state.sort !== undefined;
  const hasPage = state.page !== undefined;
  const hasFilter = state.filter !== undefined;

  return hasQuery || hasSort || hasPage || hasFilter;
}

function isSimpleRootKey(key: ReleaseSearchFilterKey): key is StringRootKey | "artist.country" {
  return key !== "year";
}

function filterParamsForUrl(
  filter: ReleaseSearchFilter,
  keys: readonly ReleaseSearchFilterKey[],
): SearchRecord {
  const params: SearchRecord = {};

  for (const key of keys) {
    if (key === "year") {
      const year = rangeToParam(filter.year);
      if (year !== undefined) params.year = year;
      continue;
    }
    if (!isSimpleRootKey(key)) continue;
    const param = matcherToParam(getFilterMatcher(filter, key));
    if (param === undefined) continue;
    params[key === "artist.country" ? "origin" : key] = param;
  }

  return params;
}

function remainingFilterForUrl(
  filter: ReleaseSearchFilter,
  allowed: ReadonlySet<ReleaseSearchFilterKey>,
): ReleaseSearchFilter | undefined {
  const rest = remainingRootFilterForUrl(filter, allowed);
  if (!allowed.has("artist.country") && filter.artist?.country !== undefined) {
    rest.artist = { country: filter.artist.country };
  }
  if (!allowed.has("year") && filter.year !== undefined) rest.year = filter.year;
  if (filter.OR?.length) rest.OR = filter.OR;

  return isReleaseSearchFilterEmpty(rest) ? undefined : rest;
}

function remainingRootFilterForUrl(
  filter: ReleaseSearchFilter,
  allowed: ReadonlySet<ReleaseSearchFilterKey>,
): ReleaseSearchFilter {
  const rest: ReleaseSearchFilter = {};
  for (const key of STRING_ROOT_KEYS) {
    if (!allowed.has(key) && filter[key] !== undefined) rest[key] = filter[key];
  }
  return rest;
}

function splitFilterForUrl(
  filter: ReleaseSearchFilter | undefined,
  config: Pick<FacetedSearchUrlConfig, "keys" | "allKeys">,
): { params: SearchRecord; filter?: ReleaseSearchFilter } {
  if (!filter) return { params: {} };
  const allowed = new Set<ReleaseSearchFilterKey>(config.keys);

  return {
    params: filterParamsForUrl(filter, config.keys),
    filter: remainingFilterForUrl(filter, allowed),
  };
}

function controlledFacetedSearchKeys(config: FacetedSearchUrlConfig): ReadonlySet<string> {
  const controlledKeys = new Set<string>(["filter", "sort", "page"]);
  for (const key of config.keys) {
    controlledKeys.add(key === "artist.country" ? "origin" : key);
  }
  return controlledKeys;
}

function applyFacetedSearchState(
  result: SearchRecord,
  next: FacetedSearchState | undefined,
  config: FacetedSearchUrlConfig,
) {
  const split = splitFilterForUrl(next?.filter, config);
  Object.assign(result, split.params);
  if (split.filter) result.filter = split.filter;
  if (next?.sort && next.sort !== config.defaultSort) result.sort = next.sort;
  if (next?.page && next.page > 1) result.page = next.page;
}

export function writeFacetedSearchState(
  prev: SearchRecord,
  next: FacetedSearchState | undefined,
  config: FacetedSearchUrlConfig,
): SearchRecord {
  const result: SearchRecord = { ...prev };
  for (const key of controlledFacetedSearchKeys(config)) {
    result[key] = undefined;
  }
  applyFacetedSearchState(result, next, config);
  return result;
}

function compactSearchRecord(record: SearchRecord): SearchRecord {
  return Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined));
}

function removeFilterKeys(
  filter: ReleaseSearchFilter | undefined,
  excludedKeys: ReadonlySet<ReleaseSearchFilterKey>,
): ReleaseSearchFilter | undefined {
  if (!filter) return undefined;
  const next = filterWithoutExcludedRootKeys(filter, excludedKeys);
  const OR = remainingOrFilters(filter.OR, excludedKeys);
  if (OR.length) next.OR = OR;
  return isReleaseSearchFilterEmpty(next) ? undefined : next;
}

function filterWithoutExcludedRootKeys(
  filter: ReleaseSearchFilter,
  excludedKeys: ReadonlySet<ReleaseSearchFilterKey>,
): ReleaseSearchFilter {
  const next: ReleaseSearchFilter = {};
  for (const key of STRING_ROOT_KEYS) {
    if (!excludedKeys.has(key) && filter[key] !== undefined) next[key] = filter[key];
  }
  if (!excludedKeys.has("artist.country") && filter.artist?.country !== undefined) {
    next.artist = { country: filter.artist.country };
  }
  if (!excludedKeys.has("year") && filter.year !== undefined) next.year = filter.year;
  return next;
}

function remainingOrFilters(
  filters: ReleaseSearchFilter[] | undefined,
  excludedKeys: ReadonlySet<ReleaseSearchFilterKey>,
): ReleaseSearchFilter[] {
  if (!filters) return [];
  return filters
    .map((child) => removeFilterKeys(child, excludedKeys))
    .filter((child): child is ReleaseSearchFilter => !isReleaseSearchFilterEmpty(child));
}

export function getWorkReleaseHandoffSearch({
  excludeKeys = [],
  filter,
  identityFilter,
  q,
}: {
  excludeKeys?: readonly ReleaseSearchFilterKey[];
  filter?: Filter;
  identityFilter?: ReleaseSearchFilter;
  q?: string | null;
}): SearchRecord {
  const workFilter: ReleaseSearchFilter = {};
  const publicFilter = filterToReleaseSearchFilter(filter, {
    allTermKeys: ALL_MATCH_FILTER_KEYS,
  });
  const excludedKeys = new Set(excludeKeys);

  mergeFilter(
    workFilter,
    removeFilterKeys(pickReleaseSearchFilter(publicFilter, WORK_RELEASE_URL_CONFIG), excludedKeys),
  );
  mergeFilter(
    workFilter,
    removeFilterKeys(
      pickReleaseSearchFilter(identityFilter, WORK_RELEASE_URL_CONFIG),
      excludedKeys,
    ),
  );

  const handoffSearch = writeFacetedSearchState(
    {},
    {
      filter: isReleaseSearchFilterEmpty(workFilter) ? undefined : workFilter,
    },
    WORK_RELEASE_URL_CONFIG,
  );

  // A missing format already means "all" on the work page; keep the handoff on the canonical URL.
  return compactSearchRecord({
    ...handoffSearch,
    q: q || undefined,
  });
}
